import { Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { format } from 'date-fns';
import { SubSink } from 'subsink';
import { PpsUtilityService } from '../services/pps-utility.service';
import { IOpenProjectViewModel } from '../shared/models/open-project-view-model';
import { OpenProjectService } from '../services/open-project.service';
import { HtmlTooltipComponent } from './html-tooltip.component';
import { DeleteActionComponent } from '../system-settings/delete-action.component';
import { MenuItem } from 'primeng/api/menuitem';
import { ConfirmationService } from 'primeng/api';
import { ToastService } from '../shared/toast/toast.service';
import { ColDef, GridApi, GridOptions, GridReadyEvent, RowDragEndEvent } from 'ag-grid-community';
import { AuthenticationService } from '../services/authentication.service';
import { BusinessOperation } from '../shared/models/business-operation';
import { Chart } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { DialogService } from 'primeng/dynamicdialog';
import { OpenProjectAddDialogComponent } from './open-project-add-dialog.component';
import { UIChart } from 'primeng/chart';
import { IOpenProjectExcelExportModel } from './models/open-project-excel-export-model';

@Component({
  selector: 'pps-open-project',
  templateUrl: './open-project.component.html',
  styles: [
    `
    :host {
      display: block;
      height: 100%;
    }

    .open-project-layout {
      height: 100%;
      display: flex;
      flex-direction: column;
      min-height: 0;
    }

    .open-project-body {
      flex: 1 1 auto;
      display: flex;
      flex-direction: column;
      min-height: 0;
    }

    .open-project-content {
      flex: 1 1 auto;
      min-height: 0;
      display: flex;
      flex-direction: column;
    }

    .chart-container {
      height: 190px;
      flex: 0 0 190px;
      margin-bottom: 6px;
      padding: 8px 8px 8px 8px;
      border: 1px solid #d7dbe0;
      border-radius: 4px;
      background-color: #f7f9fc;
      box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.6);
      display: flex;
      flex-direction: column;
      min-height: 0;
    }

    .chart-summary {
      display: flex;
      gap: 8px;
      margin-bottom: 2px;
    }

    .chart-plot {
      flex: 1 1 auto;
      min-height: 0;
    }

    .summary-chip {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      border: 1px solid #d6dbe3;
      border-radius: 9999px;
      padding: 2px 10px;
      font-size: 0.9rem;
      background: #ffffff;
      color: #374151;
    }

    .summary-chip .dot {
      width: 8px;
      height: 8px;
      border-radius: 9999px;
      display: inline-block;
    }

    .summary-chip.invoice .dot {
      background: #2563eb;
    }

    .summary-chip.po .dot {
      background: #16a34a;
    }

    .summary-chip.ipv{
      color: #f50000;
    }

    .summary-chip.ipv .dot {
      background: #f50000
    }


    @media screen and (max-width: 767px) {
      .chart-container {
        height: 220px;
        flex: 0 0 220px;
      }

      .chart-summary {
        flex-direction: column;
        gap: 4px;
      }
    }

    .open-project-grid {
      flex: 1 1 auto;
      min-height: 0;
      width: 100%;
    }

    .modal-footer {
      flex: 0 0 auto;
    }
    `
  ],
})
export class OpenProjectComponent implements OnInit, OnDestroy {
  @ViewChild('ipvChart') ipvChart!: UIChart;

  private subs = new SubSink();
  private gridApi: GridApi<IOpenProjectViewModel> | null = null;
  
  items: MenuItem[] = [
    {label: "Project", icon: "pi-fw fas fa-tractor"},
    {label: "Open Projects", icon: "pi-fw fas fa-tasks"}    
  ];

  loading = false;
  localeText: { [key: string]: string } = {
    loadingOoo: 'Loading Open Project...',
  };

  exporting = false;

  openProjects: IOpenProjectViewModel[] = [];
  chartData: any;
  chartOptions: any;
  totalInvoiceValueSum = 0;
  totalPoAmountSum = 0;
  private isCompactChart = false;
  messages: any[] = [];
  context:any = {};
  gridHeaders: ColDef<IOpenProjectViewModel>[] = [];
  gridOptions: GridOptions<IOpenProjectViewModel> = {
    rowDragManaged: true,
    animateRows: true,
    onRowDragEnd: (event: RowDragEndEvent<IOpenProjectViewModel>) => this.onPriorityRowDragEnd(event),
    tooltipShowMode: 'standard',
    tooltipShowDelay: 0,
    components: {
      htmlTooltip: HtmlTooltipComponent
    },
    defaultColDef:{
      resizable: true,
      tooltipComponent: 'htmlTooltip',
      tooltipValueGetter: (params: any) => this.getTooltipValue(params)
    }
  };
  
  chartPlugins: any[] = []; // <-- Add this array variable

  constructor(private _openProjectService: OpenProjectService,
              private _authService: AuthenticationService,
              private _confirm: ConfirmationService,
              private _toast: ToastService,
              private _dialog: DialogService,
              public _util: PpsUtilityService) {
    Chart.register(ChartDataLabels);
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  ngOnInit(): void {
    this.context = { componentParent: this };
    this.gridHeaders = this.getColumnDefs();
    this.isCompactChart = window.innerWidth < 768;
    this.initChart();

    this.loadOpenProjects();
  }

  onGridReady(params: GridReadyEvent<IOpenProjectViewModel>) {
    this.gridApi = params.api;
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    const compact = window.innerWidth < 768;
    if (compact !== this.isCompactChart) {
      this.isCompactChart = compact;
      this.initChart();
      this.updateChartData();
    }
  }

  loadOpenProjects(){
    this.openProjects = [];
    this.updateChartData();
    this.loading = true;
  
    this.subs.sink = this._openProjectService
      .getAll()
      .subscribe(response => {
        if (response.succeed){
          this.openProjects = response.result;
          this.updateChartData();
        }
      }, () => {}, () => {
        this.loading = false;
      });
  }

  addOpenProject(){
    this._dialog.open(OpenProjectAddDialogComponent, {
      header: 'Select Projects To Add To Open Project List',
      styleClass: window.innerWidth < 768 ? 'size-100' : 'size-lg',
      contentStyle: {
        height: window.innerWidth < 768 ? '80vh' : '70vh'
      },
      draggable: true,
      data: {
        disabledProjectIds: this.openProjects.map(p => p.projectId),
        onAdded: () => this.loadOpenProjects()
      }
    });
  }

  delete(project: IOpenProjectViewModel){
    if (!project){
      return;
    }


    this._confirm.confirm({
      header: `Remove Project`,
      message: `Are you sure you want to remove ${project.projectNo} from the open projects list?`,
      icon: "fas fa-exclamation-circle icon-danger",
      defaultFocus: "accept",
      accept: () => this.deleteHelper(project)
    });
  }

  deleteHelper(project: IOpenProjectViewModel){
    this.subs.sink = this._openProjectService.delete(project)
      .subscribe(apiResponse => {
        if (apiResponse.succeed){
          var foundIndex = this.openProjects.findIndex(x => x.projectId == project.projectId);
          if (foundIndex !== -1 ){
            this.openProjects.splice(foundIndex, 1);
            this.gridApi?.setGridOption('rowData', this.openProjects);
            this.loadOpenProjects();
            this._toast.succeeded(`[${project.projectNo}] removed from open project list!`);
          }else{
            this._toast.failed(`Remove [${project.projectNo}] failed!`);
          }
        }else{
          this._toast.failed(`Failed to remove [${project.projectNo}]: ${apiResponse.errors.join("|")}`);
        }
      });
  }  

  private getColumnDefs(): ColDef<IOpenProjectViewModel>[] {
    const colDefs: ColDef<IOpenProjectViewModel>[] = [
      {
        headerName: "",
        cellRenderer: DeleteActionComponent,
        resizable: false,
        width: 50,
        hide: false,
        filter: false,        
        suppressMovable: true,
        cellStyle: {textAlign:"center"},
        pinned: true
      },      
      {
        headerName: 'Priority',
        field:'priorityNo',
        sortable: true,
        width: 70,
        rowDrag: this.canModifyOpenProject(),
        pinned: true
      },
      {
        headerName: 'Project No',
        field:'projectNo',
        width: 90,
        pinned: true
      },
      {
        headerName: 'Description',
        field:'description',
        width: 500,
        tooltipField: 'description'
      },
      {
        headerName: 'Completion Percentage',
        field:'completionPercentage',
        sortable: true,
        width: 140,
        cellRenderer: this.progressCellRenderer
      },
      {
        headerName: 'Total Invoice Value',
        field:'totalInvoiceValue',
        sortable: true,
        width: 140,
        cellStyle: this._util.getNumericCellStyle({borderRightColor:'#eeeeee'}),
        valueFormatter: (params: any) => this._util.formatCurrency(params?.value)
      },
      {
        headerName: 'PO Number',
        field:'poNumber',
        width: 100
      },
      {
        headerName: 'PO Amount',
        field:'poAmount',
        width: 120,
        cellStyle: this._util.getNumericCellStyle({borderRightColor:'#eeeeee'}),
        valueFormatter: (params: any) => this._util.formatCurrency(params?.value)
      },
      {
        headerName: 'Material Procurement Status',
        field:'materialProcurementStatus',
        width: 220
      },
      {
        headerName: 'Project Comment',
        sortable: true,
        width: 250,
        valueGetter: (params: any) => this.getFirstProjectCommentText(params?.data),
        tooltipValueGetter: (params: any) => this.getProjectCommentsTooltip(params?.data)
      }
    ];

    if (!this.canModifyOpenProject()){
      colDefs.splice(0, 1);
    }

    return colDefs;
  }

  private progressCellRenderer(params: any) {
    const value = Math.round(100 * (params.value || 0));
    return `
      <div style="display: flex; align-items: center; width: 100%; height: 100%;">
        <progress 
          value="${value}" 
          max="100" 
          style="width: 70%; height: 16px; margin-right: 8px;">
        </progress>
        <span>${value}%</span>
      </div>
    `;
  }

  private getTooltipValue(params: any): string {
    if (params == null || params.value == null) {
      return '';
    }

    if (params.colDef?.field === 'completionPercentage') {
      return `<strong>${Math.round(100 * (params.value || 0))}%</strong>`;
    }

    if (params.colDef?.field === 'totalInvoiceValue' || params.colDef?.field === 'poAmount') {
      return this._util.formatCurrency(params.value);
    }

    return String(params.value);
  }

  private getProjectCommentList(data: any): any[] {
    if (!data) {
      return [];
    }

    if (Array.isArray(data.projectComment)) {
      return data.projectComment;
    }

    return [];
  }

  private getFirstProjectCommentText(data: any): string {
    const comments = this.getProjectCommentList(data);
    if (!comments.length || !comments[0]?.comment) {
      return '';
    }

    return `${String(comments[0].comment)} (${comments[0].commentBy?.name} ${this.formatCommentDateText(comments[0].commentDate)})`;
  }

  private getProjectCommentsTooltip(data: any): string {
    const comments = this.getProjectCommentList(data);
    if (!comments.length) {
      return '';
    }

    return comments
      .map((c: any) => {
        const name = c?.commentBy?.name || `${c?.commentBy?.lastName || ''}, ${c?.commentBy?.firstName || ''}`.trim();
        const dateText = this.formatCommentDateText(c?.commentDate);
        const commentText = String(c?.comment || '').replace(/\r?\n/g, '<br>');

        return `<b><small>${name} ${dateText}:</small></b><br>${commentText}<br><br>`;
      })
      .join('');
  }

  private formatCommentDateText(value: any): string {
    if (!value) {
      return '';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return String(value);
    }

    return format(date, 'dd/MM/yyyy hh:mm:ss a');
  }

  canModifyOpenProject(): boolean{
    return this._authService.canUserDoAnyOfTheOperartions([
      BusinessOperation.ModifyOpenProject
    ]);
  }

  private initChart(): void {
    const textColor = getComputedStyle(document.documentElement).getPropertyValue('--text-color') || '#495057';
    const textColorSecondary = getComputedStyle(document.documentElement).getPropertyValue('--text-color-secondary') || '#6c757d';
    const surfaceBorder = getComputedStyle(document.documentElement).getPropertyValue('--surface-border') || '#dee2e6';

    this.chartData = {
      labels: ['Total Invoice Value', 'Total PO Amount'],
      datasets: [
        {
          data: [0, 0],
          backgroundColor: ['#2563eb', '#16a34a'],
          borderColor: ['#1d4ed8', '#15803d'],
          borderWidth: 1,
          borderRadius: 4,
          barThickness: 20
        }
      ]
    };

    this.chartPlugins = [{
      id: 'customCanvasBackgroundColor',
      beforeDraw: (chart: any) => {
        const { ctx } = chart;
        ctx.save();
        ctx.globalCompositeOperation = 'destination-over';

        // 1. Fill the White Background
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, chart.width, chart.height);

        // 2. Draw the Outer Border
        ctx.globalCompositeOperation = 'source-over';

        const borderWidth = 1; // Adjust the border width as needed
        ctx.strokeStyle = '#cbd5e1'; // Border color
        ctx.lineWidth = borderWidth;            // Border thickness
        
        // Draw the border rectangle exactly at the edges of the canvas
        ctx.strokeRect(borderWidth, borderWidth, chart.width-borderWidth*2, chart.height-borderWidth*2); 

        ctx.restore();
      }
    }];

    this.chartOptions = {
      indexAxis: this.isCompactChart ? 'x' : 'y',
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: {
          top: 0,
          bottom: 0,
          left: 4,
          right: 8
        }
      },
      plugins: {
        legend: {
          display: false
        },
        datalabels: {
          anchor: 'end',
          align: (context: any) => {
            const axis = context?.chart?.options?.indexAxis;
            return axis === 'x' ? 'top' : 'right';
          },
          offset: (context: any) => {
            const axis = context?.chart?.options?.indexAxis;
            return axis === 'x' ? 4 : 6;
          },
          color: '#1f2937',
          formatter: (value: number) => this._util.formatCurrency(value || 0),
          font: {
            size: 11,
            weight: '600'
          },
          clip: false,
          clamp: true
        },
        tooltip: {
          callbacks: {
            label: (context: any) => this._util.formatCurrency(context.raw || 0)
          }
        }
      },
      scales: {
        x: {
          type: this.isCompactChart ? 'category' : 'linear',
          display: true,
          beginAtZero: true,
          ticks: {
            display: true,
            color: textColorSecondary,
            autoSkip: false,
            maxRotation: 0,
            minRotation: 0,
            padding: 4,
            callback: (value: any) => {
              if (this.isCompactChart) {
                return (this.chartData?.labels?.[Number(value)] ?? value);
              }
              return this.formatCompactCurrency(Number(value) || 0);
            }
          },
          grid: {
            color: surfaceBorder
          }
        },
        y: {
          type: this.isCompactChart ? 'linear' : 'category',
          ticks: {
            color: textColor,
            callback: (value: any) => {
              if (!this.isCompactChart) {
                return (this.chartData?.labels?.[Number(value)] ?? value);
              }
              return this.formatCompactCurrency(Number(value) || 0);
            }
          },
          grid: {
            display: false
          }
        }
      }
    };
  }

  private updateChartData(): void {
    const totalInvoiceValue = this.openProjects.reduce((sum, p) => sum + (p.totalInvoiceValue || 0), 0);
    const totalPoAmount = this.openProjects.reduce((sum, p) => sum + (p.poAmount || 0), 0);

    this.totalInvoiceValueSum = totalInvoiceValue;
    this.totalPoAmountSum = totalPoAmount ;

    this.chartData = {
      ...this.chartData,
      datasets: [
        {
          ...this.chartData.datasets[0],
          data: [totalInvoiceValue, totalPoAmount]
        }
      ]
    };
  }

  private onPriorityRowDragEnd(event: RowDragEndEvent<IOpenProjectViewModel>): void {
    if (!this.canModifyOpenProject() || !event?.api) {
      return;
    }

    const originalOrder = this.openProjects.map(p => ({ ...p }));

    const reordered: IOpenProjectViewModel[] = [];
    event.api.forEachNode((node) => {
      if (node.data) {
        reordered.push({ ...node.data });
      }
    });

    if (!reordered.length) {
      return;
    }

    reordered.forEach((project, index) => {
      project.priorityNo = index + 1;
    });

    this.openProjects = reordered;
    this.gridApi?.setGridOption('rowData', this.openProjects);

    this.subs.sink = this._openProjectService.reorder(this.openProjects)
      .subscribe(apiResponse => {
        if (apiResponse.succeed) {
          this._toast.succeeded('Priority order updated successfully.');
        } else {
          this.openProjects = originalOrder;
          this.gridApi?.setGridOption('rowData', this.openProjects);
          this._toast.failed(`Failed to save priority order: ${apiResponse.errors.join('|')}`);
        }
      }, () => {
        this.openProjects = originalOrder;
        this.gridApi?.setGridOption('rowData', this.openProjects);
        this._toast.failed('Failed to save priority order. Please try again.');
      });
  }

  private formatCompactCurrency(value: number): string {
    const abs = Math.abs(value || 0);
    if (abs >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}m`;
    }
    if (abs >= 1000) {
      return `$${(value / 1000).toFixed(0)}k`;
    }
    return this._util.formatCurrency(value);
  }

  exportOpenProject(): void {
    if (!this.openProjects.length) {
      this._toast.failed('No open projects to export.');
      return;
    }

    const canvas = this.ipvChart?.getCanvas() as HTMLCanvasElement;
    const base64Image = canvas?.toDataURL('image/png');
    const cleanBase64Image = base64Image?.replace(/^data:image\/(png|jpg);base64,/, '');

    const payload: IOpenProjectExcelExportModel = {
      chartImage: cleanBase64Image || '',
      totalPoAmount: this.totalPoAmountSum,
      totalInvoiceValue: this.totalInvoiceValueSum,
      openProjects: this.openProjects
    }
    
    this.exporting = true;
    this._openProjectService.exportOpenProjects(payload, () => {
      this._toast.succeeded('Open projects exported successfully.');
    }).add(() => {
      this.exporting = false;
    });
  }
}
